/**
 * Themenwahl eines Fachs – die eine Stelle, an der entschieden wird, welches
 * Thema gemeint ist.
 *
 * Laufen in einem Fach mehrere Themen gleichzeitig (nicht archiviert, Prüfung
 * nicht vorbei), lässt sich das nicht erraten: dann fragt ein kurzes Fenster
 * nach. Auf dem Handy fährt es von unten hoch (das Modal ist dort ohnehin eine
 * Bodenleiste). Gibt es nur ein Thema, erscheint gar nichts.
 *
 * Das Hauptthema des Fachs (Stern auf der Startseite) steht oben und ist
 * vorausgewählt.
 */

import { h } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { openModal } from "@/components/ui";
import { topicsService } from "@/services";
import { openTopics } from "@/services/topics.service";
import { subjectIcon } from "@/lib/subjectIcons";
import { defaultColor } from "@/lib/colors";
import type { Subject, Topic } from "@/types/models";

export interface TopicChoice {
  subject: Pick<Subject, "id" | "title"> & { color?: string; icon?: string; mainTopic?: string };
  /** Schon geladene Themen des Fachs; sonst werden sie hier geholt. */
  topics?: Topic[];
  title?: string;
}

/** Kandidaten in Anzeigereihenfolge: Hauptthema zuerst, dann nach Titel. */
function candidatesOf(topics: Topic[], mainTopicId?: string): Topic[] {
  const list = openTopics(topics);
  return [...list].sort((a, b) => {
    if (a.id === mainTopicId) return -1;
    if (b.id === mainTopicId) return 1;
    return a.title.localeCompare(b.title, "de");
  });
}

/**
 * Liefert das gemeinte Thema – ohne Rückfrage, wenn es nur eines gibt.
 * `undefined` heißt: abgebrochen oder gar kein Thema vorhanden.
 */
export async function chooseTopic(choice: TopicChoice): Promise<Topic | undefined> {
  const all =
    choice.topics ?? (await topicsService.listBySubject(choice.subject.id).catch(() => []));
  const candidates = candidatesOf(all, choice.subject.mainTopic);
  if (candidates.length === 1) return candidates[0];
  if (!candidates.length) {
    // Kein laufendes Thema – dann wenigstens das zuletzt genutzte anbieten.
    const fallback = all.filter((t) => t.state !== "archived");
    return fallback.length <= 1 ? fallback[0] : pick(choice, fallback);
  }
  return pick(choice, candidates);
}

function pick(choice: TopicChoice, candidates: Topic[]): Promise<Topic | undefined> {
  const accent = choice.subject.color || defaultColor();
  return new Promise((resolve) => {
    let picked: Topic | undefined;
    const list = h(
      "div",
      { class: "tt-pick-list" },
      ...candidates.map((topic) =>
        h(
          "button",
          {
            class: "tt-pick" + (topic.id === choice.subject.mainTopic ? " active" : ""),
            type: "button",
            style: `--accent:${accent}`,
            onclick: () => {
              picked = topic;
              handle.close();
            },
          },
          h("span", { class: "row", html: subjectIcon(choice.subject.icon, 16) }),
          h("span", { class: "tt-pick-label" }, topic.title),
          topic.id === choice.subject.mainTopic
            ? h("span", { class: "tt-pick-main row", title: "Hauptthema", html: icon("star", 14) })
            : null
        )
      )
    );

    const handle = openModal({
      title: choice.title ?? choice.subject.title,
      body: list,
      guardUnsaved: false,
      onClose: () => resolve(picked),
    });
  });
}
